import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { getUsers, createUser, updateUser, deleteUser, resetUserPassword, USER_ROLES, errorMessage } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { useFieldErrors } from '../lib/useFieldErrors';
import { FieldError } from '../components/FieldError';
import { LoadError } from '../components/LoadError';
import { 
    Plus, 
    Users as UsersIcon, 
    Edit,
    Trash2,
    Loader2,
    Shield,
    Mail,
    KeyRound
} from 'lucide-react';

export default function Users() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [editingUser, setEditingUser] = useState(null);
    const [resetUser, setResetUser] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [resetting, setResetting] = useState(false);
    const [saving, setSaving] = useState(false);
    const { errors, fail, clearError, resetErrors, fieldProps } = useFieldErrors();
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        name: '',
        role: 'user'
    });
    const { user: currentUser } = useAuth();

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            setLoadError(null);
            const response = await getUsers();
            setUsers(response.data);
        } catch (error) {
            if (!error._isAuthError) setLoadError(errorMessage(error, 'Erreur lors du chargement des utilisateurs'));
        } finally {
            setLoading(false);
        }
    };

    const openCreateDialog = () => {
        resetErrors();
        setEditingUser(null);
        setFormData({ email: '', password: '', name: '', role: 'user' });
        setDialogOpen(true);
    };

    const openEditDialog = (user) => {
        resetErrors();
        setEditingUser(user);
        setFormData({ email: user.email, password: '', name: user.name, role: user.role });
        setDialogOpen(true);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        clearError(name);
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name.trim()) {
            fail('name', 'Le nom est requis');
            return;
        }

        if (!editingUser) {
            if (!formData.email.trim()) {
                fail('email', 'L\'email est requis');
                return;
            }
            if (!formData.password || formData.password.length < 8) {
                fail('password', 'Le mot de passe doit contenir au moins 8 caractères');
                return;
            }
        }

        setSaving(true);

        try {
            if (editingUser) {
                await updateUser(editingUser.id, {
                    name: formData.name,
                    role: formData.role
                });
                toast.success('Utilisateur mis à jour');
            } else {
                await createUser(formData);
                toast.success('Utilisateur créé');
            }
            setDialogOpen(false);
            fetchUsers();
        } catch (error) {
            toast.error(errorMessage(error, 'Erreur lors de l\'enregistrement'));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        try {
            await deleteUser(deleteId);
            toast.success('Utilisateur supprimé');
            setDeleteId(null);
            fetchUsers();
        } catch (error) {
            toast.error(errorMessage(error, 'Erreur lors de la suppression'));
        }
    };

    const handleResetPassword = async () => {
        if (!resetUser || !newPassword) return;
        if (newPassword.length < 8) {
            fail('new-password', 'Le mot de passe doit contenir au moins 8 caractères');
            return;
        }
        setResetting(true);
        try {
            await resetUserPassword(resetUser.id, newPassword);
            toast.success(`Mot de passe de ${resetUser.name} réinitialisé`);
            setResetUser(null);
            setNewPassword('');
        } catch (error) {
            toast.error(errorMessage(error, 'Erreur lors de la réinitialisation'));
        } finally {
            setResetting(false);
        }
    };

    const getRoleBadgeColor = (role) => {
        switch (role) {
            case 'admin': return 'bg-primary/20 text-primary border-primary/30';
            case 'manager': return 'bg-tone-client-base/20 text-tone-client border-tone-client-base/30';
            default: return 'bg-tone-neutre-base/20 text-tone-neutre border-tone-neutre-base/30';
        }
    };

    const renderUserActions = (user) => (
        <div className="flex gap-1">
            <Button
                variant="ghost"
                size="icon" aria-label={`Modifier ${user.name}`}
                className="h-8 w-8"
                onClick={() => openEditDialog(user)}
                title="Modifier"
            >
                <Edit className="w-4 h-4" />
            </Button>
            <Button
                variant="ghost"
                size="icon" aria-label={`Réinitialiser le mot de passe de ${user.name}`}
                className="h-8 w-8"
                onClick={() => { resetErrors(); setResetUser(user); setNewPassword(''); }}
                title="Réinitialiser le mot de passe"
                data-testid={`reset-password-${user.id}`}
            >
                <KeyRound className="w-4 h-4" />
            </Button>
            {user.id !== currentUser?.id && (
                <Button
                    variant="ghost"
                    size="icon" aria-label={`Supprimer ${user.name}`}
                    className="h-8 w-8 text-muted-foreground hover:text-destructive-text"
                    onClick={() => setDeleteId(user.id)}
                >
                    <Trash2 className="w-4 h-4" />
                </Button>
            )}
        </div>
    );

    return (
        <Layout>
            <div className="p-4 sm:p-6 space-y-6 animate-fade-in" data-testid="users-page">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-3xl font-bold tracking-tight">
                            UTILISATEURS
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Gestion des accès de votre équipe
                        </p>
                    </div>
                    <Button onClick={openCreateDialog} data-testid="new-user-button">
                        <Plus className="w-4 h-4 mr-2" />
                        Nouvel utilisateur
                    </Button>
                </div>

                {/* Users Table */}
                {loading ? (
                    <Skeleton className="h-96" />
                ) : loadError ? (
                    <LoadError message={loadError} onRetry={() => { setLoading(true); fetchUsers(); }} />
                ) : users.length === 0 ? (
                    <Card className="bg-card border-border">
                        <CardContent className="flex flex-col items-center justify-center py-16">
                            <UsersIcon className="w-16 h-16 text-muted-foreground/50 mb-4" />
                            <h3 className="font-heading text-xl font-semibold mb-2">
                                Aucun utilisateur
                            </h3>
                            <p className="text-muted-foreground text-center max-w-md mb-6">
                                Ajoutez des membres à votre équipe.
                            </p>
                            <Button onClick={openCreateDialog}>
                                <Plus className="w-4 h-4 mr-2" />
                                Créer un utilisateur
                            </Button>
                        </CardContent>
                    </Card>
                ) : (
                    <>
                    <ul className="sm:hidden space-y-2" aria-label="Utilisateurs">
                        {users.map((user) => (
                            <li key={user.id} className="p-3 rounded-lg border border-border bg-card" data-testid={`user-card-${user.id}`}>
                                <div className="flex items-start gap-3">
                                    <div className="flex items-center justify-center w-8 h-8 shrink-0 rounded-full bg-primary/20 text-primary font-medium text-sm" aria-hidden="true">
                                        {user.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium">
                                            {user.name}
                                            {user.id === currentUser?.id && (
                                                <span className="text-muted-foreground text-xs ml-2">(vous)</span>
                                            )}
                                        </p>
                                        <p className="text-sm text-muted-foreground break-all">{user.email}</p>
                                        <Badge variant="outline" className={`mt-2 text-xs ${getRoleBadgeColor(user.role)}`}>
                                            <Shield className="w-3 h-3 mr-1" aria-hidden="true" />
                                            {USER_ROLES[user.role] || user.role}
                                        </Badge>
                                    </div>
                                </div>
                                <div className="mt-2 flex justify-end">{renderUserActions(user)}</div>
                            </li>
                        ))}
                    </ul>
                    <Card className="hidden sm:block bg-card border-border overflow-hidden">
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                                        <TableHead className="font-bold text-xs uppercase tracking-wider">Nom</TableHead>
                                        <TableHead className="font-bold text-xs uppercase tracking-wider">Email</TableHead>
                                        <TableHead className="font-bold text-xs uppercase tracking-wider">Rôle</TableHead>
                                        <TableHead className="font-bold text-xs uppercase tracking-wider w-[120px]">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {users.map((user) => (
                                        <TableRow 
                                            key={user.id} 
                                            className="hover:bg-muted/30"
                                            data-testid={`user-row-${user.id}`}
                                        >
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/20 text-primary font-medium text-sm">
                                                        {user.name.charAt(0).toUpperCase()}
                                                    </div>
                                                    <span className="font-medium">
                                                        {user.name}
                                                        {user.id === currentUser?.id && (
                                                            <span className="text-muted-foreground text-xs ml-2">(vous)</span>
                                                        )}
                                                    </span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                                    <Mail className="w-3 h-3" />
                                                    {user.email}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className={`text-xs ${getRoleBadgeColor(user.role)}`}>
                                                    <Shield className="w-3 h-3 mr-1" />
                                                    {USER_ROLES[user.role] || user.role}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                {renderUserActions(user)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </Card>
                    </>
                )}
            </div>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading">
                            {editingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
                        </DialogTitle>
                        <DialogDescription>
                            {editingUser 
                                ? 'Modifiez les informations de l\'utilisateur'
                                : 'Ajoutez un nouveau membre à votre équipe'
                            }
                        </DialogDescription>
                    </DialogHeader>
                    <form noValidate onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="name">Nom *</Label>
                            <Input
                                id="name"
                                {...fieldProps('name')}
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Jean Dupont"
                                required
                                data-testid="user-name-input"
                            />
                            <FieldError id="name" message={errors['name']} />
                        </div>
                        {!editingUser && (
                            <>
                                <div className="space-y-2">
                                    <Label htmlFor="email">Email *</Label>
                                    <Input
                                        id="email"
                                        autoComplete="off"
                                        {...fieldProps('email')}
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="jean@example.com"
                                        required
                                        data-testid="user-email-input"
                                    />
                                    <FieldError id="email" message={errors['email']} />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="password">Mot de passe *</Label>
                                    <Input
                                        id="password"
                                        autoComplete="new-password"
                                        {...fieldProps('password')}
                                        name="password"
                                        type="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        placeholder="••••••••"
                                        required
                                        data-testid="user-password-input"
                                    />
                                    <FieldError id="password" message={errors['password']} />
                                </div>
                            </>
                        )}
                        <div className="space-y-2">
                            <Label htmlFor="f-role">Rôle *</Label>
                            <Select 
                                value={formData.role} 
                                onValueChange={(v) => setFormData(prev => ({ ...prev, role: v }))}
                            >
                                <SelectTrigger id="f-role" data-testid="user-role-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(USER_ROLES).filter(([key]) => key !== 'superadmin').map(([key, label]) => (
                                        <SelectItem key={key} value={key}>{label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                Annuler
                            </Button>
                            <Button type="submit" disabled={saving} data-testid="user-submit-button">
                                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                {editingUser ? 'Enregistrer' : 'Créer'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Dialog */}
            <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer cet utilisateur ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Cette action est irréversible. L'utilisateur n'aura plus accès à l'application.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Supprimer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Reset Password Dialog */}
            <Dialog open={!!resetUser} onOpenChange={() => setResetUser(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading flex items-center gap-2">
                            <KeyRound className="w-5 h-5" />
                            Réinitialiser le mot de passe
                        </DialogTitle>
                        <DialogDescription>
                            Définissez un nouveau mot de passe pour {resetUser?.name} ({resetUser?.email})
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="new-password">Nouveau mot de passe *</Label>
                            <Input
                                id="new-password"
                                autoComplete="new-password"
                                {...fieldProps('new-password')}
                                type="password"
                                value={newPassword}
                                onChange={(e) => { setNewPassword(e.target.value); clearError('new-password'); }}
                                placeholder="Minimum 8 caractères"
                                data-testid="reset-password-input"
                            />
                            <FieldError id="new-password" message={errors['new-password']} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setResetUser(null)}>
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleResetPassword} 
                            disabled={resetting || newPassword.length < 8}
                            data-testid="reset-password-confirm"
                        >
                            {resetting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Réinitialiser
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
